import { MenuItem } from "@/app/ui/navigation/types";
import TasksMenuItem from "@/components/controls/tasks/TasksMenuItem.vue";
import { EnumExternalCalendarProvider } from "@/app/services/generated/enums/enumExternalCalendarProvider.generated";
import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import { hasPermissionsUsingUserStore } from "@/components/pages/administration/user_roles/UserAccessHandler";
import { RouteBuilder } from "@/app/ui/navigation/routeBuilder";
import { keys } from "@/plugins/i18n";
import { groupToKvp, isNotNullOrUndefined, toMap } from "@/app/apiutils";
import {
  flattenMenu,
  showMenuItemForCurrentUser,
} from "@/app/ui/navigation/menuItems";
import userStore from "@/store/modules/userStore";

const builder = new RouteBuilder("Main_");

export const mainRoutes = {
  home: builder.route({
    url: "/home",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "common" */ "@/components/pages/dashboard/Dashboard.vue"
      ),
  }),
  listFavourites: builder.route({
    url: "/favourites/favourites-list",
    view: () =>
      import(
        /* webpackChunkName: "favourites" */ "@/pages/favourites/FavouritesListPage.vue"
      ),
  }),
  brewmanNews: builder.route({
    url: "/news",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "common" */ "@/components/pages/news/BrewmanNews.vue"
      ),
  }),
  calendar: builder.route({
    url: "/calendar",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "common" */ "@/components/pages/calendar/Calendar.vue"
      ),
  }),
  calendarGoogleSuccess: builder.route({
    url: "/calendar-link-success/google",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "common" */ "@/components/pages/calendar/IntegrationLinkCallbackDialog.vue"
      ),
    componentProps: (route) => ({
      state: route.query.state,
      code: route.query.code,
      provider: EnumExternalCalendarProvider.google,
      integration: "calendar",
    }),
  }),
  calendarMicrosoftSuccess: builder.route({
    url: "/calendar-link-success/microsoft",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "common" */ "@/components/pages/calendar/IntegrationLinkCallbackDialog.vue"
      ),
    componentProps: (route) => ({
      state: route.query.state,
      code: route.query.code,
      provider: EnumExternalCalendarProvider.microsoft,
      integration: "calendar",
    }),
  }),
  tasks: builder.route({
    url: "/tasks",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "tasks" */ "@/components/pages/tasks/ViewTasks.vue"
      ),
  }),
  search: builder.routeWithQuery(
    {
      url: "/search",
      view: (): Promise<any> =>
        import(
          /* webpackChunkName: "common" */ "@/components/pages/search/SearchResults.vue"
        ),
      componentProps: (route) => ({
        q: route.query?.q.toString() ?? "",
      }),
    },
    (q: string) => ({ q })
  ),
  googleSheetSuccess: builder.route({
    url: "/sheet-link-success",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "common" */ "@/components/pages/calendar/IntegrationLinkCallbackDialog.vue"
      ),
    componentProps: (route) => ({
      state: route.query.state,
      code: route.query.code,
      provider: EnumExternalCalendarProvider.google,
      integration: "sheets",
    }),
  }),
};

export const buildMainMenuItems: () => MenuItem[] = () => [
  MenuItem.forRoute(keys.home, mainRoutes.home, {
    icon: "Home",
  }),
  MenuItem.forBranch({
    translationKey: keys.navigation_favourites.value,
    icon: "Star",
    // Favourites Sub-Menus are late-populated, after all other menus have been created
    // as the favourites link to other menu items.
    subMenus: [],
  }),
  MenuItem.forRoute(keys.calendar, mainRoutes.calendar, {
    icon: "CalendarMonth",
    visible: () =>
      hasPermissionsUsingUserStore([EnumUserPermission.menuCalendarView]),
  }),
  MenuItem.forRoute(keys.tasks, mainRoutes.tasks, {
    icon: "FormatListBulleted",
    menuComponent: TasksMenuItem,
    visible: () =>
      hasPermissionsUsingUserStore([EnumUserPermission.menuTasksView]),
  }),
];

export const findFavouritesMenuHolder = (
  primaryMenuItems: readonly MenuItem[]
) =>
  primaryMenuItems.find(
    (x) => x.translationKey === keys.navigation_favourites.value
  );

export const choosableMenuFavourites = (items: readonly MenuItem[]) =>
  groupToKvp(flattenMenu([], items), (x) => x.menuItem.id)
    .filter((x) => x.key && x.values.length === 1)
    .flatMap((x) => x.values)
    .filter(
      (flat) =>
        flat.menuItem.id &&
        flat.menuItem.view &&
        flat.menuItem.url &&
        showMenuItemForCurrentUser(flat.menuItem) &&
        flat.path.every((x) => showMenuItemForCurrentUser(x))
    );

export const buildFavouritesMenuContents = (
  primaryMenuItems: readonly MenuItem[]
): MenuItem[] => {
  const allOptions = choosableMenuFavourites(primaryMenuItems);
  const allOptionsById = toMap(allOptions, (x) => x.menuItem.id);

  const favouriteLinks: MenuItem[] = userStore.state.favourites
    .filter((x) => allOptionsById.get(x.menuIdentifier))
    .map((x) => {
      const menuItem = allOptionsById.get(x.menuIdentifier)?.menuItem;
      return menuItem && menuItem.view && menuItem.url
        ? MenuItem.fromOpts({
            id: "",
            url: menuItem.url,
            view: menuItem.view,
            breadcrumbParentName: menuItem.breadcrumbParentName,
            label: x.displayAs || menuItem.displayText(),
            visible: menuItem.visible,
          })
        : null;
    })
    .filter(isNotNullOrUndefined);
  return [
    ...favouriteLinks,
    MenuItem.forRoute(
      keys.favourites_manage_favourites,
      mainRoutes.listFavourites
    ),
  ];
};
